/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 *
 * @file ResourceCard.jsx
 * @author Srinivasan Bakthavatchalam
 * @since 1.0.1
 */

import React, { memo, useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Tooltip from '@material-ui/core/Tooltip';
import { Icon } from '@greenville/mui-theme';
import { withStyles } from '@material-ui/core/styles';

import ResourceCardStyles from './ResourceCardStyles';
import ResourceCardThumbnail from './ResourceCardThumbnail';
import ResourceUtils from '../../utils/ResourceUtils';
import StatusIcon from '../common/StatusIcon';
import { DOWNLOAD_STATUS_COMPLETED, DOWNLOAD_STATUS_ERROR } from '../../common/constants';


const ResourceCard = ({
  bulkDownloadStatus,
  classes,
  currentPageId,
  isFilterDropDownOpen,
  isLastItem,
  isPageLevel,
  isSelected,
  onResourceClick,
  resourceItem,
  setSelectedResources,
  showDownload
}) => {
  /**
   * Handles the resource item click
   */
  const handleClick = () => {
    onResourceClick(resourceItem);
  };

  useEffect(() => {
    // Considering the current page id is sent only for page level resource
    if (currentPageId && currentPageId.length && isLastItem) {
      ResourceUtils.handleScroll(currentPageId);
    }
  }, [currentPageId]);

  /**
   * Handles select checkbox click
   *
   * @param {*} event
   * @param {*} resourceItem
   */
  const handleSelectClick = (event, resource) => {
    setSelectedResources(resource);
    event.stopPropagation();
  };

  return (
    <Button
      className={`${classes.resourceCardItem} pageId-${resourceItem.pageId}`}
      color="default"
      disabled={isFilterDropDownOpen || ResourceUtils.isBulkDownloadInProgress(bulkDownloadStatus)}
      onClick={handleClick}
      variant="text"
    >
      <Card className="resource-card">
        <CardContent className="resource-card-item-heading">
          <div
            className={`resource-card-item-heading-name ${isFilterDropDownOpen ? 'item-disable' : 'item-enabled'}`}
          >
            {resourceItem.name}
          </div>
          <div className="resource-card-item-flex-grow" />
          {showDownload &&
            <CardActions className="resource-card-actions-downloadable">
              <div className="resource-card-item-downloadable">
                {ResourceUtils.isResourceDownloadable(resourceItem) && <StatusIcon
                  bulkDownloadStatus={bulkDownloadStatus}
                  selected={isSelected}
                  isFilterDropDownOpen={isFilterDropDownOpen}
                  handleClick={(event) => handleSelectClick(event, resourceItem)}
                  resource={resourceItem}
                  status={resourceItem.status}
                />}
              </div>
            </CardActions>}
        </CardContent>
        <CardActions className="resource-card-actions">
          <div className="resource-card-action-item">
            <ResourceCardThumbnail contentType={resourceItem.contentTypeValue} />
            <div className="resource-card-action-item-information">
              <div>{isPageLevel && `Page ${resourceItem.pageId}`}</div>

              <div>
                {(resourceItem.status === DOWNLOAD_STATUS_COMPLETED
                  || resourceItem.status === DOWNLOAD_STATUS_ERROR) && !isSelected && showDownload &&
                  <span className={classes.selectedSpan}>
                    <Icon
                      color={resourceItem.status === DOWNLOAD_STATUS_COMPLETED ? 'inherit' : 'error'}
                      name={resourceItem.status === DOWNLOAD_STATUS_COMPLETED ? 'Right' : 'Warning'}
                      size="small"
                    />
                  </span>}
                {!isPageLevel &&
                  <Tooltip
                    arrow
                    classes={{
                      arrow: classes.arrow,
                      tooltip: classes.tooltip
                    }}
                    title={resourceItem.path}
                  >
                    <span><Icon
                      color={isFilterDropDownOpen ? 'disabled' : 'inherit'}
                      name="Information"
                      size="small"
                      tabIndex="0"
                    />
                    </span>
                  </Tooltip>}
              </div>
            </div>
          </div>
        </CardActions>
      </Card>
    </Button>);
};


ResourceCard.propTypes = {
  bulkDownloadStatus: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  currentPageId: PropTypes.array.isRequired,
  isFilterDropDownOpen: PropTypes.bool.isRequired,
  isLastItem: PropTypes.bool,
  isPageLevel: PropTypes.bool,
  isSelected: PropTypes.bool,
  onResourceClick: PropTypes.func.isRequired,
  resourceItem: PropTypes.object,
  setSelectedResources: PropTypes.func.isRequired,
  showDownload: PropTypes.bool.isRequired
};
ResourceCard.defaultProps = {
  resourceItem: {},
  isLastItem: false,
  isPageLevel: false,
  isSelected: false
};

export default withStyles(ResourceCardStyles)(memo(ResourceCard));
